import React from 'react';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, formValueSelector, Field } from 'redux-form';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
// data
import {
  eventFaqDefinition,
  formattedMiscEventFaqDefinitions
} from '../shared-components/faq/data/eventFaqDefinition';
import onEventFaqSubmit from './data/onEventFaqSubmit';
import { EVENT_ACTIVITIES_FORM, EVENT_FAQ_CONST } from '../shared-components/faq/data/constants';
import { onEventFaqBlur, onEventFaqChange, onEventMiscItemsChange } from './data/onEventFaqChange';
import { hasQuantityAndDescription, mapBoolToString, pageInfo, seedValuesWithSelected } from './data/utils';
// faq component
import ToggleFieldRow from '../app-faq-components/ToggleFieldRow';
import FaqHeader from '../app-faq-components/FaqHeader';
import ButtonNavigation from '../app-faq-components/ButtonNavigation';
// shared component
import CheckboxField from '../shared-components/reduxForms/fields/CheckboxField/CheckboxField';
import TextAreaField from '../shared-components/reduxForms/fields/TextAreaField/TextAreaField';
const {
  OTHER_PROHIBITED_ACTIVITY
} = EVENT_FAQ_CONST;
const EventActivitiesForm = ({
  error,
  handleSubmit,
  intl,
  page,
  hasOtherProhibitedActivity,
  spaceId,
  accessToken,
  showErrorToast,
  ...props
}) => {
  const header = intl.formatMessage(pageInfo(page).header);
  const Icon = pageInfo(page).icon;
  const eventActivities = get(eventFaqDefinition, 'event_activities', {});
  const {
    [OTHER_PROHIBITED_ACTIVITY]: otherProhibitedActivity,
    ...prohibitedActivities
  } = get(eventActivities, 'prohibited_activities', {});
  const prohibitedActivitiesInfo = get(eventActivities, 'prohibited_activities_info', {});
  const onChangeFn = onEventFaqChange(props.dispatch, spaceId, accessToken, showErrorToast);
  const onChangeMiscFn = onEventMiscItemsChange(props.dispatch, spaceId, accessToken, showErrorToast);
  const onBlurFn = onEventFaqBlur(props.dispatch, spaceId, accessToken, showErrorToast);

  return (
    <form className="flex-basis-100" onSubmit={handleSubmit}>
      <FaqHeader header={header} icon={<Icon />} />
      <h6 className="questions-section">
        <FormattedMessage
          id="hosting.event_activities_form.activities_available"
          defaultMessage="Activities available during booking"
        />
      </h6>
      {
        Object.keys(eventActivities.activities_provided).map((key) => (
          <>
            <ToggleFieldRow
              label={eventActivities.activities_provided[key]}
              formName={EVENT_ACTIVITIES_FORM}
              fieldName={key}
              tooltip={eventActivities.activities_provided[key].tooltip}
              placeholder={eventActivities.activities_provided[key].placeholder}
              hasQuantity={props[`${key}_quantity`]}
              hasDescription={props[`${key}_description`]}
              dispatch={props.dispatch}
              onChange={onChangeFn}
              onBlur={onBlurFn}
            />
            <hr />
          </>
        ))
      }
      <div className="text-small text-gray-600">
        { formattedMiscEventFaqDefinitions.amenities_note }
      </div>
      <div className="h6 questions-section">
        <FormattedMessage
          id="hosting.event_activities_form.prohibited_activities_visibility_header"
          defaultMessage="Limit visibility for activities you prefer not to host"
        />
        <div className="text-small text-gray-600 sans-serif">
          <FormattedMessage
            id="hosting.event_activities_form.prohibited_activities_visibility_warning"
            defaultMessage="Your space will appear less prominent in search results for selected activities, and your listing page will indicate these are activities you prefer not to host."
          />
        </div>
      </div>
      <div>
        {
          Object.keys(prohibitedActivities).map((key) => (
            <>
              <Field
                className={!prohibitedActivitiesInfo[key] ? 'margin-lg-bottom' : ''}
                component={CheckboxField}
                name={key}
                id={key}
                key={key}
                option={'true'}
                label={intl.formatMessage(prohibitedActivities[key])}
                onChange={onChangeFn}
              />
              { prohibitedActivitiesInfo[key] && <div className="text-small text-gray-600 sans-serif margin-xl-left margin-lg-bottom">
                {intl.formatMessage(prohibitedActivitiesInfo[key])}
              </div>}
              <hr />
            </>
          ))
        }
        <Field
          className="margin-lg-bottom"
          component={CheckboxField}
          name={`${OTHER_PROHIBITED_ACTIVITY}_toggle`}
          id={`${OTHER_PROHIBITED_ACTIVITY}_toggle`}
          option={true}
          onChange={onChangeMiscFn}
          label={intl.formatMessage(otherProhibitedActivity)}
        />
        {
          hasOtherProhibitedActivity &&
            <Field
              name={OTHER_PROHIBITED_ACTIVITY}
              component={TextAreaField}
              attributes={{
                'data-testing-id': OTHER_PROHIBITED_ACTIVITY
              }}
              placeholder={intl.formatMessage(otherProhibitedActivity.placeholder)}
              maxLength={1000}
              onBlur={onBlurFn}
            />
        }
        <hr style={{ marginBottom: '24px' }} />
      </div>
      <div className="text-danger text-small margin-xl-bottom">{error}</div>
      <ButtonNavigation {...props} spaceId={spaceId} />
    </form>
  );
};

EventActivitiesForm.propTypes = {
  handleSubmit: PropTypes.func,
  error: PropTypes.string,
  dispatch: PropTypes.func,
  parkingToggleFieldValue: PropTypes.string,
  intl: intlShape,
  page: PropTypes.string,
  hasQuantityAndDescription: PropTypes.bool,
  hasOtherProhibitedActivity: PropTypes.bool,
  spaceId: PropTypes.string,
  accessToken: PropTypes.string,
  showErrorToast: PropTypes.bool
};

const config = {
  form: EVENT_ACTIVITIES_FORM,
  onSubmit: onEventFaqSubmit
};

const selector = formValueSelector(EVENT_ACTIVITIES_FORM);
function mapStateToProps (state, { eventFaq }) {
  const hasOtherProhibitedActivity = !!(selector(state, 'other_prohibited_activity_toggle'));
  const initialValues = mapBoolToString(seedValuesWithSelected(eventFaq));
  const { other_prohibited_activity, ...restOfValues } = initialValues;
  const eventActivities = get(eventFaqDefinition, 'event_activities', {});
  const itemSelected = hasQuantityAndDescription(Object.keys(eventActivities.activities_provided), selector, state);
  return {
    hasOtherProhibitedActivity,
    ...itemSelected,
    initialValues: {
      ...restOfValues,
      other_prohibited_activity,
      other_prohibited_activity_toggle: !!other_prohibited_activity,
    }
  };
}

export default connect(mapStateToProps)(reduxForm(config)(injectIntl(EventActivitiesForm)));
